import { css } from "@emotion/react";
import { useState, useMemo } from "react";
import { Link } from "react-router";

export function AvailableEvaluationsCard({
  projects,
  evaluators,
  timeRangeLabel,
}: {
  projects: any[];
  evaluators: Array<{ name: string }>;
  timeRangeLabel?: string;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState<"all" | "active" | "unexecuted">(
    "all"
  );
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);

  // Derive the metric list from the real evaluator catalog and the annotation
  // results actually recorded across projects. No hardcoded/fabricated values.
  const evaluatorMetrics = useMemo(() => {
    const normalize = (name: string) =>
      name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

    const prettify = (name: string) =>
      name
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());

    // Aggregate real annotation results (trace- and span-level) per name.
    const aggregateByName = new Map<
      string,
      { name: string; count: number; weightedScore: number }
    >();
    const addResult = (
      rawName: string,
      meanScore: number | null | undefined,
      count: number | null | undefined
    ) => {
      const numRuns = count ?? 0;
      const key = normalize(rawName);
      if (numRuns <= 0 || !key) return;
      const raw = meanScore ?? 1;
      const score = Math.min(1, Math.max(0, raw > 1 ? raw / 100 : raw));
      const existing =
        aggregateByName.get(key) ??
        { name: rawName, count: 0, weightedScore: 0 };
      existing.count += numRuns;
      existing.weightedScore += score * numRuns;
      aggregateByName.set(key, existing);
    };

    projects.forEach((p) => {
      (p?.traceAnnotationSummaries ?? []).forEach(
        (summary: {
          name: string;
          meanScore?: number | null;
          count?: number;
        }) => addResult(summary.name, summary.meanScore, summary.count)
      );
      (p?.spanAnnotationAgentSummaries ?? []).forEach(
        (summary: {
          name: string;
          meanScore?: number | null;
          numScores?: number;
        }) => addResult(summary.name, summary.meanScore, summary.numScores)
      );
    });

    // Catalog of available evaluators (deduped by normalized name).
    const catalog = new Map<string, string>();
    evaluators.forEach((evaluator) => {
      const key = normalize(evaluator.name);
      if (key && !catalog.has(key)) {
        catalog.set(key, evaluator.name);
      }
    });
    // Surface any executed annotation that is absent from the catalog so real
    // results are never hidden.
    aggregateByName.forEach((value, key) => {
      if (!catalog.has(key)) {
        catalog.set(key, value.name);
      }
    });

    return Array.from(catalog.entries())
      .map(([key, rawName]) => {
        const aggregate = aggregateByName.get(key);
        if (aggregate && aggregate.count > 0) {
          const avgScore = aggregate.weightedScore / aggregate.count;
          const passed = Math.round(aggregate.count * avgScore);
          const failed = aggregate.count - passed;
          const passRate = Math.round(avgScore * 100);
          return {
            name: prettify(rawName),
            status: "executed" as const,
            passed,
            failed,
            total: aggregate.count,
            passRate: `${passRate}%`,
            passPctNum: passRate,
          };
        }
        return {
          name: prettify(rawName),
          status: "not_executed" as const,
          passed: 0,
          failed: 0,
          total: 0,
          passRate: "N/A",
          passPctNum: 0,
        };
      })
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === "executed" ? -1 : 1;
        }
        if (a.status === "executed" && b.status === "executed") {
          return b.total - a.total;
        }
        return a.name.localeCompare(b.name);
      });
  }, [projects, evaluators]);

  // Filtering
  const filteredMetrics = useMemo(() => {
    return evaluatorMetrics.filter((m) => {
      const matchesSearch = m.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      if (filterTab === "active") return m.status === "executed";
      if (filterTab === "unexecuted") return m.status === "not_executed";
      return true;
    });
  }, [evaluatorMetrics, searchQuery, filterTab]);

  const activeCount = evaluatorMetrics.filter(
    (e) => e.status === "executed"
  ).length;
  const unexecutedCount = evaluatorMetrics.filter(
    (e) => e.status === "not_executed"
  ).length;

  return (
    <div
      css={css`
        background: #ffffff;
        border: 1px solid var(--global-border-color-default, #e1e3e4);
        border-radius: 16px;
        padding: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        box-sizing: border-box;
        max-width: 100%;
        overflow: hidden;
        transition: all 0.3s ease;
        &:hover {
          border-color: var(--global-color-gray-300);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
      `}
    >
      {/* Header & Legend */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          flex-wrap: wrap;
          gap: 8px;
        `}
      >
        <div>
          <div
            css={css`
              font-size: 22px;
              font-weight: 900;
              color: #000000 !important;
            `}
          >
            Available Evaluation Metrics
          </div>
          <div
            css={css`
              font-size: 14px;
              font-weight: 800;
              color: #000000 !important;
              margin-top: 2px;
            `}
          >
            Trace evaluation templates status across projects (
            {evaluatorMetrics.length} total)
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 13px;
            font-weight: 900;
            color: #000000 !important;
          `}
        >
          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 4px;
              color: #065f46;
            `}
          >
            <span
              css={css`
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: #10b981;
              `}
            />{" "}
            Passed / Avg
          </span>
          <span
            css={css`
              display: inline-flex;
              align-items: center;
              gap: 4px;
              color: #000000 !important;
            `}
          >
            <span
              css={css`
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: #cbd5e1;
              `}
            />{" "}
            Not Executed
          </span>
        </div>
      </div>

      {/* Filter Tabs & Search Box */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 4px;
            background: #f1f5f9;
            padding: 3px;
            border-radius: 8px;
          `}
        >
          <button
            onClick={() => setFilterTab("all")}
            css={css`
              border: none;
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 900;
              cursor: pointer;
              background: ${filterTab === "all" ? "#ffffff" : "transparent"};
              color: ${filterTab === "all" ? "#00685f" : "#000000"};
              box-shadow: ${filterTab === "all"
                ? "0 1px 2px rgba(0,0,0,0.06)"
                : "none"};
            `}
          >
            All ({evaluatorMetrics.length})
          </button>
          <button
            onClick={() => setFilterTab("active")}
            css={css`
              border: none;
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 900;
              cursor: pointer;
              background: ${filterTab === "active" ? "#ffffff" : "transparent"};
              color: ${filterTab === "active" ? "#00685f" : "#000000"};
              box-shadow: ${filterTab === "active"
                ? "0 1px 2px rgba(0,0,0,0.06)"
                : "none"};
            `}
          >
            Active ({activeCount})
          </button>
          <button
            onClick={() => setFilterTab("unexecuted")}
            css={css`
              border: none;
              padding: 4px 10px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 900;
              cursor: pointer;
              background: ${filterTab === "unexecuted"
                ? "#ffffff"
                : "transparent"};
              color: ${filterTab === "unexecuted" ? "#00685f" : "#000000"};
              box-shadow: ${filterTab === "unexecuted"
                ? "0 1px 2px rgba(0,0,0,0.06)"
                : "none"};
            `}
          >
            Unexecuted ({unexecutedCount})
          </button>
        </div>

        <input
          type="text"
          placeholder="Filter evaluators..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          css={css`
            border: 1px solid #cbd5e1;
            border-radius: 20px;
            padding: 5px 14px;
            font-size: 12px;
            font-weight: 800;
            outline: none;
            width: 170px;
            background: #ffffff;
            color: #0f172a;
            &:focus {
              border-color: #00685f;
              box-shadow: 0 0 0 2px rgba(0, 104, 95, 0.15);
            }
          `}
        />
      </div>

      {/* Horizontal Scrollable Bar Chart Grid */}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          gap: 14px;
          height: 205px;
          width: 100%;
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 6px 6px 6px 6px;
          box-sizing: border-box;
          margin-bottom: 8px;
          align-items: flex-end;

          &::-webkit-scrollbar {
            height: 8px;
          }
          &::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 4px;
          }
          &::-webkit-scrollbar-thumb {
            background: #00685f;
            border-radius: 4px;
          }
          &::-webkit-scrollbar-thumb:hover {
            background: #004d40;
          }
        `}
      >
        {filteredMetrics.length > 0 ? (
          filteredMetrics.map((ev) => {
            const isExecuted = ev.status === "executed";
            const passPct = isExecuted
              ? Math.round((ev.passed / ev.total) * 100)
              : 0;
            const failPct = isExecuted ? 100 - passPct : 0;
            const isHovered = hoveredMetric === ev.name;

            return (
              <div
                key={ev.name}
                onMouseEnter={() => setHoveredMetric(ev.name)}
                onMouseLeave={() => setHoveredMetric(null)}
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: flex-end;
                  flex-shrink: 0;
                  width: 54px;
                  height: 100%;
                  position: relative;
                `}
              >
                {/* Hover Breakdown Popover */}
                {isHovered && isExecuted && (
                  <div
                    css={css`
                      position: absolute;
                      top: 0;
                      left: 50%;
                      transform: translateX(-50%);
                      z-index: 20;
                      font-size: 10px;
                      font-weight: 700;
                      color: #334155;
                      display: flex;
                      flex-direction: column;
                      gap: 2px;
                      background: #ffffff;
                      padding: 6px 8px;
                      border-radius: 6px;
                      border: 1px solid #cbd5e1;
                      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
                      white-space: nowrap;
                    `}
                  >
                    <span css={css`font-weight: 800; color: #0f172a;`}>
                      {ev.name}
                    </span>
                    <span css={css`color: #059669;`}>
                      Passed: {ev.passed} ({passPct}%)
                    </span>
                    <span
                      css={css`
                        color: ${ev.failed > 0 ? "#f59e0b" : "#475569"};
                        font-weight: ${ev.failed > 0 ? 800 : 700};
                      `}
                    >
                      Failed: {ev.failed} ({failPct}%)
                    </span>
                    <span css={css`color: #475569;`}>
                      Total: {ev.total} runs
                    </span>
                  </div>
                )}

                {/* Percentage on top */}
                <span
                  css={css`
                    font-size: 11px;
                    font-weight: 800;
                    color: ${isExecuted
                      ? ev.failed > 0
                        ? "#10b981"
                        : "#10b981"
                      : "#94a3b8"};
                    margin-bottom: 6px;
                  `}
                >
                  {isExecuted ? ev.passRate : "N/A"}
                </span>

                {/* Vertical Bar Representation */}
                <div
                  title={`${ev.name}: ${isExecuted ? `${ev.passed} passed / ${ev.failed} failed` : "Not Executed"}`}
                  css={css`
                    height: 160px;
                    width: 24px;
                    border-radius: 6px;
                    background: #f1f5f9;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-end;
                    cursor: pointer;
                    border: 1px solid ${isHovered ? "#00685f" : "#e2e8f0"};
                    transition: border-color 0.2s ease;
                  `}
                >
                  {isExecuted ? (
                    <>
                      {/* Yellow Bar segment for Failed runs */}
                      {ev.failed > 0 && (
                        <div
                          css={css`
                            height: ${Math.max(failPct, 4)}%;
                            width: 100%;
                            background: #f59e0b;
                            flex-shrink: 0;
                            border-radius: 2px 2px 0 0;
                          `}
                        />
                      )}
                      {/* Green Bar segment for Passed runs */}
                      <div
                        css={css`
                          height: ${ev.failed > 0
                            ? 100 - Math.max(failPct, 4)
                            : 100}%;
                          width: 100%;
                          background: #10b981;
                          flex-shrink: 0;
                          border-radius: ${ev.failed > 0
                            ? "0 0 4px 4px"
                            : "4px"};
                          transition: height 0.3s ease;
                        `}
                      />
                    </>
                  ) : (
                    <div
                      css={css`
                        height: 100%;
                        width: 100%;
                        background: #cbd5e1;
                        border-radius: 4px;
                      `}
                    />
                  )}
                </div>

                {/* Evaluator Name at Bottom */}
                <span
                  title={ev.name}
                  css={css`
                    font-size: 10px;
                    font-weight: 700;
                    color: ${isExecuted ? "#0f172a" : "#94a3b8"};
                    margin-top: 8px;
                    text-align: center;
                    width: 54px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  {ev.name.split(" ")[0]}
                </span>
              </div>
            );
          })
        ) : (
          <div
            css={css`
              font-size: 12px;
              font-weight: 600;
              color: #64748b;
              padding: 20px;
              text-align: center;
              width: 100%;
            `}
          >
            No evaluators match "{searchQuery}"
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        css={css`
          margin-top: 4px;
          padding-top: 12px;
          border-top: 1px solid #e1e3e4;
          font-size: 13px;
          color: #000000 !important;
          font-weight: 800;
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      >
        <span>
          {evaluatorMetrics.length} Total Evaluators ({activeCount} Active,{" "}
          {unexecutedCount} Unexecuted)
        </span>
        <Link
          to="/evaluators"
          css={css`
            color: #00685f !important;
            font-weight: 900;
            font-size: 14px;
            cursor: pointer;
            text-decoration: none;
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          View all evaluators →
        </Link>
      </div>
    </div>
  );
}
